import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { MoreFeaturedNews as MoreFeaturedNewsComponent, MoreFeaturedNewsItem }
from '@fuww/library/src/Articles/Sections/Featured/MoreFeaturedNews';
import Border from '@fuww/library/src/Articles/Sections/Border';
import breakpoints from '@fuww/library/src/Theme/breakpoints';
import CardWithLink from '../../../CardWithLink';
import withQuery from '../../../../lib/withQuery';
import articlesQuery from '../../../../lib/queries/news/articlesQuery.graphql';
import getArticlesField from '../../../../lib/queries/news/getArticlesField';
import { useSiteContext } from '../../../SiteContext';
import messages from '../../../../lib/messages.mjs';
import useNoCacheOnServerFetchPolicy
from '../../../../lib/useNoCacheOnServerFetchPolicy';
import getFeaturedVariables from '../../../../lib/getFeaturedVariables';

const MoreNewsCard = ({
  article,
  variables,
  showCreatedAt,
  indicateFeatured,
}) => (
  <CardWithLink
    newsArticle={article}
    variables={variables}
    sizes={`
      (min-width: 1440px) 318px,
      (min-width: ${breakpoints.lg}px) 23vw,
      (min-width: ${breakpoints.sm}px) 47vw,
      100vw
    `}
    showCreatedAt={showCreatedAt}
    indicateFeatured={indicateFeatured}
    withPageMargins
  />
);

MoreNewsCard.propTypes = {
  article: PropTypes.shape({}).isRequired,
  variables: PropTypes.shape({}).isRequired,
  showCreatedAt: PropTypes.bool,
  indicateFeatured: PropTypes.bool,
};

MoreNewsCard.defaultProps = {
  showCreatedAt: false,
  indicateFeatured: true,
};

const MoreFeaturedArticles = ({
  data,
  variables,
  articlesField,
  showCreatedAt,
}) => {
  const articles = data[articlesField];
  return (
    <>
      {articles.map((article) => (
        <MoreFeaturedNewsItem key={article.id}>
          <MoreNewsCard
            article={article}
            variables={variables}
            showCreatedAt={showCreatedAt}
            indicateFeatured={false}
          />
        </MoreFeaturedNewsItem>
      ))}
    </>
  );
};

MoreFeaturedArticles.propTypes = {
  data: PropTypes.shape({}).isRequired,
  variables: PropTypes.shape({}).isRequired,
  articlesField: PropTypes.string.isRequired,
  showCreatedAt: PropTypes.bool,
};

MoreFeaturedArticles.defaultProps = {
  showCreatedAt: false,
};

const MoreFeaturedArticlesWithQuery = withQuery(MoreFeaturedArticles);
const errorMessage = 'Error loading news articles...';

const MoreFeaturedNews = ({
  variables, showCreatedAt, notInIds, skip,
}) => {
  const intl = useIntl();
  const { prefix, hasLocalNews, newsboard } = useSiteContext();
  const fetchPolicy = useNoCacheOnServerFetchPolicy();

  const field = getArticlesField(prefix, hasLocalNews);

  return (
    <>
      <Border />
      <MoreFeaturedNewsComponent
        categoryHeading={intl.formatMessage(messages.featured)}
        moreFeaturedNewsArticles={(
          <MoreFeaturedArticlesWithQuery
            query={articlesQuery}
            variables={getFeaturedVariables(
              prefix,
              hasLocalNews,
              newsboard,
              notInIds,
              variables,
            )}
            skip={skip}
            errorMessage={errorMessage}
            articlesField={field}
            showCreatedAt={showCreatedAt}
            fetchPolicy={fetchPolicy}
          />
        )}
      />
    </>
  );
};

MoreFeaturedNews.propTypes = {
  variables: PropTypes.shape({}),
  showCreatedAt: PropTypes.bool,
  notInIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  skip: PropTypes.bool,
};

MoreFeaturedNews.defaultProps = {
  variables: null,
  showCreatedAt: false,
  skip: false,
};

export default MoreFeaturedNews;
